// dataFetcher.js
/**
 * Loads all class data from individual class files with improved error handling
 * @param {string} baseUrl - Base URL for API requests
 * @returns {Array} Array of class data objects
 */
async function loadAllClasses(baseUrl) {
  try {
    if (!baseUrl) {
      console.error('Invalid baseUrl provided to loadAllClasses');
      return [];
    }
    
    console.log(`Fetching class index from: ${baseUrl}/data/class/index.json`);
    const indexResponse = await fetch(`${baseUrl}/data/class/index.json`);
    
    if (!indexResponse.ok) {
      console.error(`Failed to load class index: ${indexResponse.status}`);
      return [];
    }
    
    let classIndex;
    try {
      classIndex = await indexResponse.json();
    } catch (jsonError) {
      console.error('Error parsing class index JSON:', jsonError);
      return [];
    }
    
    // Validate class index structure
    if (!classIndex || typeof classIndex !== 'object') {
      console.error('Invalid class index format:', classIndex);
      return [];
    }
    
    const classNames = Object.keys(classIndex);
    console.log(`Found ${classNames.length} classes in index`);
    
    if (classNames.length === 0) {
      return [];
    }
    
    const classPromises = classNames.map(async (className) => {
      try {
        const classFileName = classIndex[className];
        if (!classFileName) {
          console.error(`Missing filename for class: ${className}`);
          return null;
        }
        
        const classUrl = `${baseUrl}/data/class/${classFileName}`;
        console.log(`Fetching class: ${classUrl}`);
        
        const response = await fetch(classUrl);
        if (!response.ok) {
          console.error(`Failed to load class ${className}: ${response.status}`);
          return null;
        }
        
        const classData = await response.json();
        console.log(`Loaded raw data for class ${className}:`, JSON.stringify(classData).substring(0, 200) + '...');
        
        // Handle different possible class data structures
        let processedClass = null;
        
        // Case 1: Class data is in an array under the 'class' property
        if (classData && Array.isArray(classData.class) && classData.class.length > 0) {
          processedClass = classData.class[0]; // Take the first item
          console.log(`Found class ${className} in class array`);
        }
        // Case 2: Class data is directly in the 'class' property as an object
        else if (classData && classData.class && typeof classData.class === 'object' && !Array.isArray(classData.class)) {
          processedClass = classData.class;
          console.log(`Found class ${className} in class property`);
        }
        // Case 3: Class data is under a property with the same name as the class
        else if (classData && classData[className] && typeof classData[className] === 'object') {
          processedClass = classData[className];
          console.log(`Found class ${className} in property with same name`);
        }
        // Case 4: Try to find any object property that might contain the class data
        else if (classData && typeof classData === 'object') {
          for (const prop in classData) {
            if (classData[prop] && typeof classData[prop] === 'object') {
              processedClass = classData[prop];
              console.log(`Found class ${className} in property: ${prop}`);
              break;
            }
          }
          
          // If still not found, use the entire object
          if (!processedClass) {
            processedClass = classData;
            console.log(`Using entire object for class ${className}`);
          }
        }
        
        // If we still haven't found any data, log the issue and return null
        if (!processedClass) {
          console.error(`Could not process class data for ${className}:`, classData);
          return null;
        }
        
        // Ensure the class has all required properties
        if (!processedClass.name) {
          console.log(`Adding missing name property to class ${className}`);
          processedClass.name = className;
        }
        
        if (!processedClass.source) {
          console.log(`Adding missing source property to class ${className}`);
          processedClass.source = "PHB"; // Default source
        } else if (typeof processedClass.source !== 'string') {
          console.log(`Converting non-string source for class ${className} to string`);
          processedClass.source = String(processedClass.source);
        }
        
        console.log(`Successfully processed class ${className} with source ${processedClass.source}`);
        return processedClass;
      } catch (error) {
        console.error(`Error loading class ${className}:`, error);
        return null;
      }
    });
    
    // Wait for all class loading promises to resolve
    const results = await Promise.all(classPromises);
    
    // Remove nulls and log details for debugging
    const validClassData = results.filter(classObj => {
      if (!classObj) return false;
      
      // Log the class for debugging
      console.log(`Validating class: ${classObj.name || 'unnamed'}, source: ${classObj.source || 'unknown'}`);
      
      return true;
    });
    
    console.log(`Successfully loaded ${validClassData.length} classes`);
    return validClassData;
  } catch (error) {
    console.error('Error in loadAllClasses:', error);
    return [];
  }
}

/**
 * An improved source filter function that handles edge cases
 * @param {Object} entry - The entry to check
 * @param {Array} selectedIds - Array of selected source IDs
 * @returns {boolean} Whether the entry should be included
 */
function sourceFilter(entry, selectedIds) {
  // Log the entry for debugging
  console.log('Filtering entry:', entry);
  
  if (!entry) {
    console.log('Entry is null or undefined');
    return false;
  }
  
  // Handle the case where source could be missing or undefined
  const source = entry.source;
  if (source === undefined || source === null) {
    console.log('Entry has no source property');
    return false;
  }
  
  // Convert to string if necessary
  const sourceStr = String(source);
  const result = selectedIds.includes(sourceStr);
  
  console.log(`Source check for "${sourceStr}": ${result ? 'INCLUDED' : 'EXCLUDED'}`);
  return result;
}

/**
 * Loads source books data from the server
 * @param {string} baseUrl - Base URL for API requests
 * @returns {Array} Array of source books or empty array if failed
 */
export async function loadSources(baseUrl) {
  try {
    if (!baseUrl) {
      console.error('Invalid baseUrl provided to loadSources');
      return [];
    }
    
    console.log(`Fetching sources from: ${baseUrl}/data/books.json`);
    const response = await fetch(`${baseUrl}/data/books.json`);
    
    if (!response.ok) {
      console.error(`Failed to load sources: ${response.status} ${response.statusText}`);
      return [];
    }
    
    try {
      const data = await response.json();
      
      // Check if data is already an array
      if (Array.isArray(data)) {
        console.log(`Successfully loaded ${data.length || 0} sources as direct array`);
        return data;
      }
      
      // If data is not an array but has a property that might be the array we want
      if (data && typeof data === 'object') {
        // First check specifically for "book" property based on provided JSON structure
        if (Array.isArray(data.book)) {
          console.log(`Found sources array in property: book with ${data.book.length} entries`);
          return data.book;
        }
        
        // Try other common property names that might contain the array
        const possibleArrays = ['sources', 'data', 'items', 'source', 'sourceList', 'books'];
        for (const prop of possibleArrays) {
          if (Array.isArray(data[prop])) {
            console.log(`Found sources array in property: ${prop} with ${data[prop].length} entries`);
            return data[prop];
          }
        }
        
        // Last resort - try to find the first array property in the object
        for (const prop in data) {
          if (Array.isArray(data[prop])) {
            console.log(`Found potential sources array in property: ${prop} with ${data[prop].length} entries`);
            return data[prop];
          }
        }
      }
      
      console.error('Sources data is not in expected format:', typeof data);
      return [];
    } catch (jsonError) {
      console.error('Error parsing sources JSON:', jsonError);
      return [];
    }
  } catch (error) {
    console.error('Error in loadSources:', error);
    return [];
  }
}

/**
 * Loads all character creation data filtered by selected sources
 * @param {string} baseUrl - Base URL for API requests
 * @param {Object} selectedSourcesObj - Object mapping source IDs to boolean selection state
 * @returns {Object} Object containing races, classes, and backgrounds arrays
 */
export async function loadAllCharacterData(baseUrl, selectedSourcesObj) {
  try {
    if (!baseUrl) {
      console.error('Invalid baseUrl provided to loadAllCharacterData');
      return { races: [], classes: [], backgrounds: [] };
    }
    
    // Safely extract selected source IDs
    const selectedIds = [];
    if (selectedSourcesObj && typeof selectedSourcesObj === 'object') {
      Object.keys(selectedSourcesObj).forEach(id => {
        if (selectedSourcesObj[id] && id) {
          selectedIds.push(id);
        }
      });
    }
    
    console.log(`Loading character data for sources: ${selectedIds.join(', ') || 'none'}`);
    
    if (selectedIds.length === 0) {
      console.warn('No sources selected, returning empty data');
      return { races: [], classes: [], backgrounds: [] };
    }

    // Initialize with empty arrays in case fetches fail
    let raceData = [];
    let bgData = [];
    let classData = [];

    try {
      // Fetch race data
      console.log(`Fetching races from: ${baseUrl}/data/races.json`);
      const raceResponse = await fetch(`${baseUrl}/data/races.json`);
      if (raceResponse.ok) {
        const raceJson = await raceResponse.json();
        
        // Handle different possible data structures
        if (Array.isArray(raceJson)) {
          raceData = raceJson;
        } else if (raceJson && Array.isArray(raceJson.race)) {
          raceData = raceJson.race;
        } else if (raceJson && typeof raceJson === 'object') {
          // Try to find race data in any array property
          for (const prop in raceJson) {
            if (Array.isArray(raceJson[prop])) {
              console.log(`Found race data in property: ${prop}`);
              raceData = raceJson[prop];
              break;
            }
          }
        }
        
        console.log(`Loaded ${raceData.length} races before filtering`);
      } else {
        console.error(`Failed to load races: ${raceResponse.status}`);
      }
    } catch (raceError) {
      console.error('Error loading races:', raceError);
    }
    
    try {
      // Fetch background data
      console.log(`Fetching backgrounds from: ${baseUrl}/data/backgrounds.json`);
      const bgResponse = await fetch(`${baseUrl}/data/backgrounds.json`);
      if (bgResponse.ok) {
        const bgJson = await bgResponse.json();
        
        // Handle different possible data structures
        if (Array.isArray(bgJson)) {
          bgData = bgJson;
        } else if (bgJson && Array.isArray(bgJson.background)) {
          bgData = bgJson.background;
        } else if (bgJson && typeof bgJson === 'object') {
          // Try to find background data in any array property
          for (const prop in bgJson) {
            if (Array.isArray(bgJson[prop])) {
              console.log(`Found background data in property: ${prop}`);
              bgData = bgJson[prop];
              break;
            }
          }
        }
        
        console.log(`Loaded ${bgData.length} backgrounds before filtering`);
      } else {
        console.error(`Failed to load backgrounds: ${bgResponse.status}`);
      }
    } catch (bgError) {
      console.error('Error loading backgrounds:', bgError);
    }
    
    try {
      // Fetch class data
      console.log('Fetching class data...');
      classData = await loadAllClasses(baseUrl);
    } catch (classError) {
      console.error('Error loading classes:', classError);
    }
    
    // Filter data by selected sources with detailed logging
    const filteredRaces = Array.isArray(raceData) ? raceData.filter(item => {
      try {
        return sourceFilter(item, selectedIds);
      } catch (err) {
        console.error('Error filtering race:', err, item);
        return false;
      }
    }) : [];
    
    const filteredClasses = Array.isArray(classData) ? classData.filter(item => {
      console.log(`Checking class: ${item?.name || 'unnamed'}`);
      try {
        return sourceFilter(item, selectedIds);
      } catch (err) {
        console.error('Error filtering class:', err, item);
        return false;
      }
    }) : [];
    
    const filteredBackgrounds = Array.isArray(bgData) ? bgData.filter(item => {
      try {
        return sourceFilter(item, selectedIds);
      } catch (err) {
        console.error('Error filtering background:', err, item);
        return false;
      }
    }) : [];
    
    console.log(`After filtering: ${filteredRaces.length} races, ${filteredClasses.length} classes, ${filteredBackgrounds.length} backgrounds`);
    
    // Log the filtered classes for debugging
    console.log('Filtered classes:', filteredClasses.map(c => `${c.name} (${c.source})`));
    
    return {
      races: filteredRaces,
      classes: filteredClasses,
      backgrounds: filteredBackgrounds
    };
  } catch (error) {
    console.error('Error in loadAllCharacterData:', error);
    // Return empty data instead of throwing
    return { races: [], classes: [], backgrounds: [] };
  }
    }
